// The share landing — `/c/<token>` (Brief 3b NEEDS-5, the web lane).
//
// A share link's no-install destination: exchange the URL token via the
// existing `POST /auth/share` for a THREAD-SCOPED JWT, then render that one
// thread — read, live SSE, reply. The guest's scoped session lives in an
// in-memory store (see shareModel.ts), so an orchestrator opening a share
// link in their own browser keeps their session untouched, and the scoped
// credential dies with the tab.
//
// Renders with the SAME MessageList + chatModel mapping the miniapp uses —
// one chat vocabulary on every surface. No pause/resume controls here: a
// share guest holds none (the backend + bridge enforce it; this page simply
// doesn't offer what would 403).
//
// Mounted OUTSIDE the orchestrator sign-in gate (the token IS the guest's
// auth) but INSIDE the site code gate, which wraps every surface while the
// product is pre-launch.

import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { createClient, ApiError } from "@waytide/core";
import type { ChatMessage, WaytideClient } from "@waytide/core";
import { MessageList } from "../components/MessageList";
import { buildChatMessages, mergeIncomingMessage } from "./chatModel";
import { emailFromJwt, memoryTokenStore } from "./shareModel";
import { usePersistentDraft } from "@waytide/ui";

const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ??
  "http://localhost:3001";

type Phase =
  | { kind: "exchanging" }
  | { kind: "invalid"; detail: string }
  | { kind: "ready"; threadId: number; guestEmail: string };

export function ShareLandingPage() {
  const { token = "" } = useParams();

  // The guest's OWN client over an in-memory store — never the web app's
  // localStorage-backed singleton.
  const client: WaytideClient = useMemo(
    () => createClient({ apiBase: API_BASE, store: memoryTokenStore() }),
    [],
  );

  const [phase, setPhase] = useState<Phase>({ kind: "exchanging" });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // The guest's draft is persisted per share token (Brief 5 / point 15): a
  // reload re-exchanges the token, and the half-typed reply survives that
  // round-trip instead of vanishing. The guest's send already keeps the draft
  // on failure (retryable) and clears it only on confirmed success.
  const { draft, setDraft, clearDraft } = usePersistentDraft(
    token.trim() === "" ? null : `share:${token.trim()}`,
  );
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  // Exchange the link token for the thread-scoped session, then load the
  // thread. A dead/expired/revoked token lands the calm invalid state.
  useEffect(() => {
    let alive = true;
    const t = token.trim();
    if (t === "") {
      setPhase({ kind: "invalid", detail: "This link is incomplete." });
      return;
    }
    client
      .authShare(t)
      .then((resp) => {
        if (!alive) return;
        client.saveSession({
          token: resp.token,
          identity: "",
          user: { email: emailFromJwt(resp.token), display_name: "guest" },
          is_admin: false,
        });
        return client.listThreadMessages(resp.thread_id).then((list) => {
          if (!alive) return;
          setMessages(list.messages);
          setPhase({
            kind: "ready",
            threadId: resp.thread_id,
            guestEmail: emailFromJwt(resp.token),
          });
        });
      })
      .catch((err) => {
        if (!alive) return;
        setPhase({
          kind: "invalid",
          detail:
            err instanceof ApiError
              ? "This share link is no longer valid — ask for a fresh one."
              : "Could not reach the session. Check your connection and reload.",
        });
      });
    return () => {
      alive = false;
    };
  }, [client, token]);

  // The live leg: the scoped token's stream carries exactly this thread.
  useEffect(() => {
    if (phase.kind !== "ready") return;
    return client.subscribeChat(
      (m) => {
        if (m.thread_id === phase.threadId) {
          setMessages((prev) => mergeIncomingMessage(prev, m));
        }
      },
      () => {},
    );
  }, [client, phase]);

  // Keep the newest message in view as the thread grows.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages]);

  const view = useMemo(
    () =>
      phase.kind === "ready"
        ? buildChatMessages(messages, phase.guestEmail)
        : [],
    [messages, phase],
  );

  const send = () => {
    if (phase.kind !== "ready") return;
    const body = draft.trim();
    if (body === "" || sending) return;
    setSending(true);
    client
      .sendThreadMessage(phase.threadId, body)
      .then((m) => {
        setMessages((prev) => mergeIncomingMessage(prev, m));
        clearDraft(); // cleared only on confirmed success
      })
      .catch(() => {}) // draft stays put — retryable, and persisted
      .finally(() => setSending(false));
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="flex h-[560px] w-[380px] flex-col overflow-hidden border border-edge bg-surface">
        <header className="flex h-11 shrink-0 items-center border-b border-edge px-4">
          <span className="text-[0.8125rem] font-medium text-primary">
            Shared session
          </span>
          <span className="ml-auto text-[0.6875rem] text-faint">
            view &amp; reply
          </span>
        </header>

        {phase.kind === "exchanging" && (
          <p className="p-4 text-[0.8125rem] text-faint" data-testid="share-loading">
            Opening the shared session…
          </p>
        )}

        {phase.kind === "invalid" && (
          <p className="p-4 text-[0.8125rem] text-muted" data-testid="share-invalid">
            {phase.detail}
          </p>
        )}

        {phase.kind === "ready" && (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto px-3">
              {view.length === 0 ? (
                <p className="p-4 text-[0.8125rem] text-faint">
                  Nothing here yet — say hello.
                </p>
              ) : (
                <MessageList messages={view} />
              )}
              <div ref={bottomRef} />
            </div>
            <form
              className="flex shrink-0 items-center gap-2 border-t border-edge p-2"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Reply…"
                className="min-w-0 flex-1 border border-edge bg-surface-raised px-3 py-1.5 text-[0.8125rem] text-primary placeholder:text-faint focus:border-edge-strong focus:outline-none"
                data-testid="share-composer"
                aria-label="Reply"
              />
              <button
                type="submit"
                disabled={draft.trim() === "" || sending}
                className="shrink-0 border border-edge px-2.5 py-1.5 text-[0.75rem] text-muted transition-colors hover:text-primary disabled:opacity-50"
              >
                Send
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
